import { useEffect, useMemo, useState } from 'react'
import { Alert, App, Button, Select, Space, Typography } from 'antd'
import { CalculatorOutlined, CheckOutlined, EditOutlined } from '@ant-design/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { Can } from '@/shared/auth/Can'
import { formatDate } from '@/shared/lib/format'
import { formatMoneyValue } from '@/shared/lib/money'
import { PageHeader } from '@/shared/ui/PageHeader'
import { StatTiles } from '@/shared/ui/StatTiles'
import { fetchCostCenters, fetchPeriods } from '@/features/finance/financeApi'
import { DistributionsTable } from '@/features/stakeholder/DistributionsTable'
import { ManualDistributionModal } from '@/features/stakeholder/ManualDistributionModal'
import {
  FULL_SHARE_BP,
  approveDistributions,
  bpToPercent,
  calculateRun,
  fetchRun,
  fetchStakeholders,
} from '@/features/stakeholder/stakeholderApi'

const COMPANY = 'COMPANY'

/** Oy va doira bo'yicha foyda taqsimoti: hisoblash, ko'rib chiqish, tasdiqlash, to'lov. */
export function StakeholderDistributionsPage() {
  const { t } = useTranslation()
  const { message, modal } = App.useApp()
  const queryClient = useQueryClient()
  const [periodId, setPeriodId] = useState<string | null>(null)
  const [scope, setScope] = useState<string>(COMPANY)
  const [selected, setSelected] = useState<string[]>([])
  const [manual, setManual] = useState(false)
  const orgNodeId = scope === COMPANY ? null : scope

  const periods = useQuery({ queryKey: queryKeys.finance.periods, queryFn: ({ signal }) => fetchPeriods(signal) })
  const centers = useQuery({ queryKey: queryKeys.finance.costCenters, queryFn: ({ signal }) => fetchCostCenters(signal) })
  const stakeholders = useQuery({
    queryKey: queryKeys.stakeholder.list('ACTIVE'),
    queryFn: ({ signal }) => fetchStakeholders('ACTIVE', signal),
  })

  useEffect(() => {
    if (periodId !== null || !periods.data) return
    const lastClosed = periods.data.find((p) => p.state === 'CLOSED')
    if (lastClosed) setPeriodId(lastClosed.id)
  }, [periods.data, periodId])

  const run = useQuery({
    queryKey: queryKeys.stakeholder.run(periodId ?? '', orgNodeId),
    queryFn: ({ signal }) => fetchRun(periodId ?? '', orgNodeId, signal),
    enabled: periodId !== null,
  })

  useEffect(() => setSelected([]), [periodId, scope])

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.stakeholder.all })
    void queryClient.invalidateQueries({ queryKey: queryKeys.finance.all })
  }

  const calculate = useMutation({
    mutationFn: () => calculateRun(periodId ?? '', orgNodeId),
    onSuccess: refresh,
    onError: (error) => void message.error(translateError(error, t)),
  })

  const approveSelected = useMutation({
    mutationFn: () => approveDistributions(selected),
    onSuccess: () => {
      setSelected([])
      refresh()
    },
    onError: (error) => void message.error(translateError(error, t)),
  })

  const confirmApproveSelected = () => {
    const chosen = (run.data?.rows ?? []).filter((row) => selected.includes(row.id))
    const currency = chosen[0]?.calculated.currency
    modal.confirm({
      title: t('stakeholder.run.approveSelectedTitle', { count: chosen.length }),
      content: t(chosen[0]?.kind === 'LOSS' ? 'stakeholder.run.approveSelectedLossWarn' : 'stakeholder.run.approveSelectedWarn', {
        amount: currency
          ? formatMoneyValue({ amount: chosen.reduce((sum, row) => sum + row.calculated.amount, 0), currency })
          : t('common.dash'),
      }),
      okText: t('stakeholder.action.approve'),
      cancelText: t('common.cancel'),
      onOk: () => approveSelected.mutateAsync().catch(() => undefined),
    })
  }

  const scopeStakeholders = useMemo(
    () =>
      (stakeholders.data ?? [])
        .filter((s) => (s.scope?.id ?? null) === orgNodeId)
        .map((s) => ({ id: s.id, label: `${s.code} · ${s.name}` })),
    [stakeholders.data, orgNodeId],
  )

  const data = run.data
  const expenses = data?.profit
    ? { amount: data.profit.cogs.amount + data.profit.operating.amount + data.profit.other.amount, currency: data.profit.net.currency }
    : null

  return (
    <>
      <PageHeader
        title={t('nav.stakeholderDistributions')}
        description={t('pageIntro.stakeholderDistributions')}
        extra={
          <Space wrap>
            <Can permission="stakeholder.manage">
              <Button
                type="primary"
                icon={<CalculatorOutlined />}
                disabled={!data?.closed || data.eligibleCount === 0}
                loading={calculate.isPending}
                onClick={() => calculate.mutate()}
              >
                {t('stakeholder.run.calculate')}
              </Button>
            </Can>
            <Can permission="stakeholder.distribution.approve">
              <Button
                icon={<CheckOutlined />}
                disabled={selected.length === 0}
                loading={approveSelected.isPending}
                onClick={confirmApproveSelected}
              >
                {t('stakeholder.run.approveSelected', { number: selected.length })}
              </Button>
            </Can>
            <Can permission="stakeholder.distribution.approve">
              <Button icon={<EditOutlined />} disabled={!data?.closed} onClick={() => setManual(true)}>
                {t('stakeholder.run.manual')}
              </Button>
            </Can>
          </Space>
        }
      />

      <Space wrap style={{ marginBottom: 16 }}>
        <Select<string>
          style={{ minWidth: 200 }}
          placeholder={t('stakeholder.run.period')}
          loading={periods.isPending}
          value={periodId ?? undefined}
          onChange={setPeriodId}
          options={(periods.data ?? []).map((p) => ({
            value: p.id,
            label: `${p.code} · ${t(`periodState.${p.state}`, { defaultValue: p.state })}`,
          }))}
        />
        <Select<string>
          style={{ minWidth: 260 }}
          showSearch
          optionFilterProp="label"
          loading={centers.isPending}
          value={scope}
          onChange={setScope}
          options={[
            { value: COMPANY, label: t('stakeholder.scope.company') },
            ...(centers.data ?? []).map((c) => ({ value: c.id, label: `${c.code} · ${c.displayName}` })),
          ]}
        />
      </Space>

      {periods.isError ? <Alert type="error" showIcon style={{ marginBottom: 16 }} message={translateError(periods.error, t)} /> : null}
      {run.isError ? <Alert type="error" showIcon style={{ marginBottom: 16 }} message={translateError(run.error, t)} /> : null}
      {data && !data.closed ? (
        <Alert type="warning" showIcon style={{ marginBottom: 16 }} message={t('stakeholder.run.openPeriod')} />
      ) : null}
      {data && data.totalShareBp < FULL_SHARE_BP && data.closed ? (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={t('stakeholder.run.unallocated', { percent: bpToPercent(FULL_SHARE_BP - data.totalShareBp) })}
        />
      ) : null}

      {data?.profit && data.profit.net.amount < 0 ? (
        <Alert type="error" showIcon style={{ marginBottom: 16 }} message={t('stakeholder.run.lossNotice')} />
      ) : null}
      {data?.profit ? (
        <>
          <StatTiles
            tiles={[
              { key: 'revenue', title: t('stakeholder.profit.revenue'), value: formatMoneyValue(data.profit.revenue) },
              { key: 'expenses', title: t('stakeholder.profit.expenses'), value: formatMoneyValue(expenses) },
              {
                key: 'net',
                title: t('stakeholder.profit.net'),
                value: formatMoneyValue(data.profit.net),
                danger: data.profit.net.amount < 0,
              },
              {
                key: 'share',
                title: t('stakeholder.run.totalShare'),
                value: t('stakeholder.percent', { value: bpToPercent(data.totalShareBp) }),
              },
            ]}
          />
          <Typography.Paragraph type="secondary">
            {t('stakeholder.run.recordDate', { date: formatDate(data.recordDate), number: data.eligibleCount })}
          </Typography.Paragraph>
        </>
      ) : null}

      <DistributionsTable
        rows={data?.rows ?? []}
        loading={run.isPending && periodId !== null}
        showStakeholder
        selectable
        selected={selected}
        onSelect={setSelected}
      />

      <ManualDistributionModal
        open={manual}
        periodId={periodId}
        stakeholders={scopeStakeholders}
        onClose={() => setManual(false)}
      />
    </>
  )
}
